import type { MarkdownDocument } from '@markbird/shared';

/** Normalize a folder path: trim segments, drop empties, `''`→null. */
export function normalizeFolderPath(path: string | null | undefined): string | null {
  if (!path) return null;
  const parts = path
    .split('/')
    .map((s) => s.trim())
    .filter(Boolean);
  return parts.length ? parts.join('/') : null;
}

/** All folder paths present in the library, including ancestors, sorted. */
export function allFolders(docs: Pick<MarkdownDocument, 'folder'>[]): string[] {
  const set = new Set<string>();
  for (const d of docs) {
    const f = normalizeFolderPath(d.folder);
    if (!f) continue;
    const parts = f.split('/');
    for (let i = 1; i <= parts.length; i++) set.add(parts.slice(0, i).join('/'));
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}

/** Group documents by their normalized folder (null key = root). */
export function groupByFolder<T extends Pick<MarkdownDocument, 'folder'>>(
  docs: T[],
): Map<string | null, T[]> {
  const map = new Map<string | null, T[]>();
  for (const d of docs) {
    const f = normalizeFolderPath(d.folder);
    const list = map.get(f) ?? [];
    list.push(d);
    map.set(f, list);
  }
  return map;
}

export interface FolderRow {
  path: string;
  name: string;
  depth: number;
  collapsed: boolean;
}

/**
 * Folders visible given a set of collapsed paths: a folder is hidden when any
 * ancestor is collapsed. Returns display rows (name = last segment, depth).
 */
export function visibleFolders(folders: string[], collapsed: Set<string>): FolderRow[] {
  const rows: FolderRow[] = [];
  for (const path of folders) {
    const parts = path.split('/');
    const hiddenByAncestor = parts
      .slice(0, -1)
      .some((_, i) => collapsed.has(parts.slice(0, i + 1).join('/')));
    if (hiddenByAncestor) continue;
    rows.push({
      path,
      name: parts[parts.length - 1] ?? path,
      depth: parts.length - 1,
      collapsed: collapsed.has(path),
    });
  }
  return rows;
}
